import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styles from './BookingDetailsPage.module.scss';

import Loading from '../../components/ui/Loading/Loading';
import BookingDetailsContent from '../../components/BookingDetails/BookingDetailsContent';
import PaymentStatusModal from '../../components/BookingDetails/PaymentStatusModal';
import { PageContentLayout } from '../../components/ui/layout/PageContentLayout/PageContentLayout';
import PaymentIframeModal from '../../components/ui/PaymentIframeModal/PaymentIframeModal';
import { PaymentStatus } from '../../types/Booking/booking';

import { useBookingDetailsPage } from './hooks/useBookingDetailsPage';

const BookingDetailsPage: React.FC = () => {
    const { t } = useTranslation();
    const [showPaymentToast, setShowPaymentToast] = useState(true);
    const {
        bookingDetail,
        loading,
        error,
        isActionLoading,
        isSecondaryActionLoading,
        updateLoading,
        paymentStatus,
        isModalOpen,
        paymentUrl,
        handleAction,
        handleSecondaryAction,
        handleCloseModal,
        handleRetryPayment,
        updateBooking,
        handlePaymentIframeClose,
        isInIframe,
        shouldCheckPayment,
        userType,
        justPaid
    } = useBookingDetailsPage();

    const location = useLocation();

    if (error) return null;

    if (isInIframe && shouldCheckPayment) return null;

    return (
        <PageContentLayout>
            <div className={styles['booking-details-container']}>
                {loading ? (
                    <Loading fullscreen text={t('appAdmin.BookingDetailsPage.loadingBookingDetails')} />
                ) : (
                    bookingDetail && (
                        <BookingDetailsContent
                            bookingDetail={bookingDetail}
                            onAction={handleAction}
                            isActionLoading={isActionLoading}
                            onSecondaryAction={handleSecondaryAction}
                            isSecondaryActionLoading={isSecondaryActionLoading}
                            onUpdateBooking={updateBooking}
                            isUpdateLoading={updateLoading}
                            hideStatusInfo={shouldCheckPayment || !!(paymentStatus && (['captured', 'approved', 'authorized'] as PaymentStatus[]).includes(paymentStatus))}
                        />
                    )
                )}

                <PaymentStatusModal
                    open={isModalOpen}
                    status={paymentStatus}
                    onClose={handleCloseModal}
                    onRetry={handleRetryPayment}
                />

                <PaymentIframeModal
                    open={!!paymentUrl}
                    onClose={handlePaymentIframeClose}
                    url={paymentUrl}
                />

                {showPaymentToast && bookingDetail && userType === 'patient' && (
                    justPaid || location.state?.showSuccessToast
                ) && (
                    <div className={styles['payment-toast']} role="status">
                        <span className="bw-toast__icon" aria-hidden>
                            <svg viewBox="0 0 24 24"><path d="M5 12.5l4.2 4.2L19 7"></path></svg>
                        </span>
                        <div className="bw-toast__content">
                            <strong className="bw-toast__title">{t('appAdmin.BookingDetailsPage.paymentSuccessTitle')}</strong>
                            <span className="bw-toast__message">{t('appAdmin.BookingDetailsPage.paymentSuccessMessage', { professional: bookingDetail?.professional || 'el profesional' })}</span>
                        </div>
                        <button className="bw-toast__close" onClick={() => setShowPaymentToast(false)} aria-label="Close">
                            <svg viewBox="0 0 24 24" width="16" height="16"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                        </button>
                    </div>
                )}

            </div>
        </PageContentLayout>
    );
};

export default BookingDetailsPage;
